// KonexZero WebRTC peer. Adapted from the reference TestPeer in
// Convose/zerocalls/app/frontend/lib/test-demo/peer.ts.
//
// stgbee talks to KonexZero through a Rails proxy at /konexzero/api/v1/*
// so the API key never reaches the browser; the proxy enforces per-call
// auth via Call#viewable_by? and forces X-Participant-Id = "user_<id>".
//
// Flow per peer:
//   1. joinRoom() — POST /rooms/:id/join → ICE servers + ws_token
//   2. getUserMedia() — local mic + (optional) cam
//   3. publish() — addTransceiver + createOffer + setLocalDescription;
//      POST /publish with tracks + SDP; setRemoteDescription with answer;
//      wait for outbound RTP (bytesSent > 0) then POST /publish/confirm
//      (KZ Phase 10: tracks are announced to the room at confirm, not at
//      /publish — see docs/api-changelog.md in Convose/zerocalls)
//   4. subscribeTo([{participant_id, track_names, track_kinds?}]) —
//      fast path (kinds known): addTransceiver(recvonly) per track +
//      createOffer; POST /subscribe with the offer; apply the answer —
//      one round trip. Legacy path (kinds unknown): POST /subscribe;
//      setRemoteDescription(offer); createAnswer; POST /subscribe/answer
//   5. leave() — POST /leave; close PC; stop tracks
//
// Subscribe lifecycle is driven by CallChannel events: when a peer joins,
// the server broadcasts {type: 'participant_joined', kz_participant_id}
// and we call subscribeTo. When they leave, we drop their tile.

export type KonexzeroPeerLog = (level: 'request' | 'response' | 'error' | 'info', text: string, detail?: string) => void

// Stable, SDK-level connection states. Consumers map these to UI:
//   - "connected" / "live": green dot, normal operation
//   - "reconnecting": yellow banner; ICE restart in flight
//   - "lost": red banner; ICE restart attempts exhausted, user should refresh
export type KonexzeroConnectionState = 'new' | 'connected' | 'reconnecting' | 'lost' | 'closed'

// KonexZero RFC 7807 error responses carry a stable `code` slug (Phase 9+).
// We surface it on the thrown error so callers can branch on `code` instead of
// substring-matching `detail`. `status` is the HTTP status. Legacy responses
// (no `code` field) leave `code` undefined — callers should fall back to
// status-only checks.
export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  // KonexZero's typed retry hint (Retry-After-Ms header), sent with 503
  // tracks_not_ready. When present, retry after this delay instead of a
  // fixed backoff schedule.
  readonly retryAfterMs?: number
  constructor(message: string, status: number, code?: string, retryAfterMs?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.retryAfterMs = retryAfterMs
  }
}

export interface KonexzeroPeerConfig {
  basePath: string                     // e.g. "/konexzero/api/v1"
  roomKey: string
  participantId: string                // e.g. "user_<uuid>"
  displayName: string
  roomType: 'group' | '1-to-1'
  audio: boolean
  video: boolean
  onLog?: KonexzeroPeerLog
  onRemoteStream?: (peerId: string, stream: MediaStream) => void
  onRemoteTrack?: (peerId: string, track: MediaStreamTrack) => void
  onConnectionStateChange?: (state: KonexzeroConnectionState) => void
  // Optional Cloudflare SFU session id from a prior join. When set, the
  // initial joinRoom() sends it as resume_session_id so KonexZero reuses
  // the existing participant entry (peers don't see leave+rejoin and
  // chat continuity is preserved) if the upstream session is still alive.
  resumeSessionId?: string
}

interface JoinResponse {
  data: {
    room: { room_id: string; type: string; is_active: boolean; created_at: string }
    participant: { participant_id: string; display_name: string; participant_type: string }
    ws_token: string
    ice_servers: RTCIceServer[]
    // KonexZero Phase 9+ returns the SFU session_id only to the joining
    // participant so the SDK can pass it back as resume_session_id on a
    // re-join after a transient failure. Absent on legacy upstreams.
    session_id?: string
    resumed?: boolean
  }
}

interface PublishResponse {
  data: { session_description: RTCSessionDescriptionInit }
}

interface SubscribeResponse {
  data: { session_description: RTCSessionDescriptionInit }
}

// ICE-restart policy: drift before we declare lost.
// - DISCONNECTED_GRACE_MS: how long we let `disconnected` ride before we
//   trigger a restart (transient blips often self-heal)
// - MAX_ICE_RESTARTS: hard cap before we give up and emit "lost"
// - ICE_RESTART_BACKOFF_MS: wait between attempts; grows with attempt index
const DISCONNECTED_GRACE_MS = 4_000
const MAX_ICE_RESTARTS = 3
const ICE_RESTART_BACKOFF_MS = [0, 1_000, 3_000]

// Parse `a=msid:<stream-id> <track-id>` (and the legacy `a=ssrc:N msid:...`
// variant) lines out of an SDP. KonexZero/Cloudflare Calls assigns opaque
// MediaStream IDs that don't match our `user_<id>` participant convention,
// so we can't route incoming tracks by stream ID alone. But the SDP offer
// the SFU sends us on `/subscribe` DOES contain the exact stream IDs it
// will use — extracting them at subscribe time gives us a deterministic
// stream-id → participant-id ledger for ontrack routing.
//
// Returns the unique set of stream IDs found. Skips the `-` placeholder
// (meaning "no stream id assigned"), per RFC 8830.
function extractStreamIdsFromSdp(sdp: string | undefined | null): string[] {
  if (!sdp) return []
  const ids = new Set<string>()
  for (const line of sdp.split(/\r?\n/)) {
    const msid = line.match(/^a=msid:(\S+)/)
    if (msid && msid[1] !== '-') { ids.add(msid[1]); continue }
    const ssrcMsid = line.match(/^a=ssrc:\d+\s+msid:(\S+)/)
    if (ssrcMsid && ssrcMsid[1] !== '-') ids.add(ssrcMsid[1])
  }
  return Array.from(ids)
}

// getUserMedia timeout. If the browser hangs (permission prompt never
// resolves, hardware enumeration stuck, NotReadableError race), reject
// after this window so the caller can retry rather than wait forever.
const GET_USER_MEDIA_TIMEOUT_MS = 15_000

export class KonexzeroPeer {
  private cfg: KonexzeroPeerConfig
  private pc: RTCPeerConnection | null = null
  private localStream: MediaStream | null = null
  private remoteStreamsByPeer: Map<string, MediaStream> = new Map()
  private subscribedPeerIds: Set<string> = new Set()
  // Deterministic lookup: SFU-assigned stream id → the participant_id we
  // subscribed to. Populated by `subscribeTo` from the SDP offer we get
  // back. Replaces the fuzzy substring / single-peer fallback that was
  // dropping tracks silently the moment 2+ peers were subscribed.
  private streamIdToPeerId: Map<string, string> = new Map()
  private published: Record<string, RTCRtpTransceiver> = {}
  private hasJoined = false
  private hasPublished = false
  private hasLeft = false
  private iceRestartAttempts = 0
  private iceRestartInFlight = false
  private disconnectedTimer: number | null = null
  private connectionState: KonexzeroConnectionState = 'new'
  // Cached so rebuildPcForPublish() can recreate the PC after a viewer
  // is promoted without round-tripping /join again.
  private cachedIceServers: RTCIceServer[] = []
  // Serializes SDP negotiation (createOffer / setLocalDescription /
  // setRemoteDescription sequences). Publish, subscribe and ICE restart
  // can fire concurrently (cable handler + periodic sweep + state events
  // are independent triggers); interleaving two negotiations on one PC
  // throws InvalidStateError or, worse, applies an answer to the wrong
  // offer. Each negotiation chains onto the previous one's settlement.
  private negotiationChain: Promise<unknown> = Promise.resolve()
  // KonexZero Cloudflare SFU session id, returned only to the owning
  // participant on /join. Callers can read via getSessionId() and persist
  // to sessionStorage so a hard refresh can resume rather than reconnect.
  private currentSessionId: string | null = null

  constructor(cfg: KonexzeroPeerConfig) {
    this.cfg = cfg
  }

  // Join the room (server-side records the participant on KonexZero) and
  // create the RTCPeerConnection with the returned ICE servers.
  async joinRoom(): Promise<JoinResponse['data']> {
    if (this.hasJoined) throw new Error('already joined')
    // Set BEFORE the await on purpose: it guards against a concurrent second
    // joinRoom() slipping in while this request is still in flight. But it must
    // be rolled back if the request fails, or a single failed join poisons the
    // instance permanently — the guard above would then reject every retry, and
    // only constructing a fresh peer could recover. Inert today (a failed join
    // goes to 'failed' and a remount builds a new peer), which is exactly why
    // it would bite whenever retry logic is added later.
    this.hasJoined = true
    const body: Record<string, unknown> = {
      display_name: this.cfg.displayName,
      // participant_type is not enforced upstream (see KONEXZERO_GAPS N3), and
      // the publisher/viewer distinction is carried by rejoinAsPublisher /
      // rejoinAsViewer instead. Left as-is deliberately rather than guessed at.
      room_type: this.cfg.roomType,
      participant_type: 'publisher'
    }
    if (this.cfg.resumeSessionId) body.resume_session_id = this.cfg.resumeSessionId

    let res: JoinResponse
    try {
      res = await this.api<JoinResponse>('POST', '/join', body)
    } catch (err) {
      this.hasJoined = false
      throw err
    }

    this.cachedIceServers = res.data.ice_servers
    this.currentSessionId = res.data.session_id ?? null
    this.pc = this.buildPc(res.data.ice_servers)
    if (this.cfg.resumeSessionId && res.data.resumed) {
      this.cfg.onLog?.('info', `joined: resumed prior session ${this.cfg.resumeSessionId.slice(0, 12)}…`)
    }
    return res.data
  }

  // Expose the SFU session id so callers (use-call.ts) can persist it to
  // sessionStorage and pass it back via `resumeSessionId` on a hard refresh
  // or transient-failure rebuild. Null before joinRoom() or on legacy
  // upstreams that don't return it.
  getSessionId(): string | null {
    return this.currentSessionId
  }

  // Construct the RTCPeerConnection with ontrack + connection-state
  // handlers wired up. Extracted so rebuildPcForPublish() can reuse
  // exactly the same handlers when it tears down and recreates the PC.
  private buildPc(iceServers: RTCIceServer[]): RTCPeerConnection {
    const pc = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' })

    pc.ontrack = (event) => {
      // Route the incoming track to a peer tile. Strategy (in order):
      //   1. SDP-pre-registered stream id → participant_id from `subscribeTo`
      //      (deterministic — the only reliable path when 2+ peers subscribed).
      //   2. Exact match of stream id against subscribedPeerIds (works if
      //      KZ happens to use the participant_id as the stream id).
      //   3. Single-subscribed-peer fallback (1:1 calls — only one
      //      candidate, so any incoming track must be theirs).
      //   4. Fuzzy substring match against subscribedPeerIds (legacy).
      //   5. Drop the track and log loudly so we can diagnose.
      const rawStreamId = event.streams?.[0]?.id ?? ''
      let peerId: string | null = null
      let routeVia = ''

      if (rawStreamId && this.streamIdToPeerId.has(rawStreamId)) {
        peerId = this.streamIdToPeerId.get(rawStreamId) ?? null
        routeVia = 'sdp-msid'
      } else if (this.subscribedPeerIds.has(rawStreamId)) {
        peerId = rawStreamId
        routeVia = 'exact'
      } else if (this.subscribedPeerIds.size === 1) {
        peerId = Array.from(this.subscribedPeerIds)[0]
        routeVia = 'single-peer-fallback'
      } else if (rawStreamId) {
        for (const known of this.subscribedPeerIds) {
          if (rawStreamId.includes(known) || known.includes(rawStreamId)) { peerId = known; routeVia = 'fuzzy'; break }
        }
      }

      if (!peerId) {
        this.cfg.onLog?.('error',
          `dropping unmatched remote ${event.track.kind} ` +
          `(stream id "${rawStreamId}", ` +
          `subscribed=[${Array.from(this.subscribedPeerIds).join(',')}], ` +
          `streamIdLedger=[${Array.from(this.streamIdToPeerId.entries()).map(([k, v]) => k + '→' + v).join(', ')}])`
        )
        return
      }
      this.cfg.onLog?.('info', `route remote ${event.track.kind} → ${peerId} via ${routeVia} (stream id "${rawStreamId}")`)

      let stream = this.remoteStreamsByPeer.get(peerId)
      if (!stream) {
        stream = new MediaStream()
        this.remoteStreamsByPeer.set(peerId, stream)
        this.cfg.onRemoteStream?.(peerId, stream)
      }
      stream.addTrack(event.track)
      this.cfg.onRemoteTrack?.(peerId, event.track)
      this.cfg.onLog?.('info', `received remote ${event.track.kind} from ${peerId} (raw stream id: "${rawStreamId}")`)
    }

    // ICE state is finer-grained than connectionState and surfaces the
    // "stuck at checking" failure mode (subscribe SDPs exchanged but no
    // candidate pair completes — usually a TURN/NAT issue) that the
    // rolled-up connectionState hides. ICE gathering state catches the
    // "candidates never even gathered" case.
    pc.oniceconnectionstatechange = () => {
      this.cfg.onLog?.('info', `iceConnectionState=${pc.iceConnectionState}`)
    }
    pc.onicegatheringstatechange = () => {
      this.cfg.onLog?.('info', `iceGatheringState=${pc.iceGatheringState}`)
    }

    pc.onconnectionstatechange = () => {
      const cs = pc.connectionState
      this.cfg.onLog?.('info', `connectionState=${cs}`)
      // Stale handler from a torn-down PC (rebuildPcForPublish) — ignore.
      if (this.pc !== pc) return
      if (cs === 'connected') {
        this.iceRestartAttempts = 0
        if (this.disconnectedTimer != null) {
          window.clearTimeout(this.disconnectedTimer)
          this.disconnectedTimer = null
        }
        this.setConnectionState('connected')
      } else if (cs === 'disconnected') {
        // Give the network a moment to self-heal before forcing a restart.
        if (this.disconnectedTimer != null) return
        this.disconnectedTimer = window.setTimeout(() => {
          this.disconnectedTimer = null
          if (this.pc?.connectionState === 'disconnected') void this.tryIceRestart()
        }, DISCONNECTED_GRACE_MS)
      } else if (cs === 'failed') {
        if (this.disconnectedTimer != null) {
          window.clearTimeout(this.disconnectedTimer)
          this.disconnectedTimer = null
        }
        void this.tryIceRestart()
      } else if (cs === 'closed') {
        this.setConnectionState('closed')
      }
    }

    return pc
  }

  getConnectionState(): KonexzeroConnectionState {
    return this.connectionState
  }

  // Run `fn` after every previously-queued negotiation has settled.
  // Failures propagate to the caller but never poison the chain.
  private withNegotiationLock<T>(fn: () => Promise<T>): Promise<T> {
    // The stored chain is always pre-caught, so a plain .then() runs fn
    // regardless of how the previous negotiation ended.
    const run = this.negotiationChain.then(fn)
    this.negotiationChain = run.catch(() => undefined)
    return run
  }

  private setConnectionState(next: KonexzeroConnectionState) {
    if (this.connectionState === next) return
    this.connectionState = next
    this.cfg.onConnectionStateChange?.(next)
  }

  // Force-renegotiate the existing peer connection to recover from an
  // ICE failure (network change, NAT rebind, transient packet loss).
  // Called automatically on connection state `failed` or after the
  // disconnect grace window. Safe to invoke from leave() guards: the
  // hasLeft / pc-null checks short-circuit.
  private async tryIceRestart(): Promise<void> {
    if (this.hasLeft || !this.pc || this.iceRestartInFlight) return
    if (this.iceRestartAttempts >= MAX_ICE_RESTARTS) {
      this.cfg.onLog?.('error', `ICE restart cap reached (${MAX_ICE_RESTARTS}); marking connection lost`)
      this.setConnectionState('lost')
      return
    }
    this.iceRestartInFlight = true
    const attempt = this.iceRestartAttempts
    this.iceRestartAttempts += 1
    this.setConnectionState('reconnecting')

    const wait = ICE_RESTART_BACKOFF_MS[Math.min(attempt, ICE_RESTART_BACKOFF_MS.length - 1)]
    if (wait > 0) await new Promise((r) => setTimeout(r, wait))
    if (this.hasLeft || !this.pc) { this.iceRestartInFlight = false; return }

    try {
      this.cfg.onLog?.('info', `ICE restart attempt ${attempt + 1}/${MAX_ICE_RESTARTS}`)
      await this.withNegotiationLock(async () => {
        if (this.hasLeft || !this.pc) return
        const offer = await this.pc.createOffer({ iceRestart: true })
        await this.pc.setLocalDescription(offer)
        const res = await this.api<PublishResponse>('POST', '/renegotiate', {
          session_description: { type: offer.type, sdp: offer.sdp }
        })
        await this.pc.setRemoteDescription(res.data.session_description)
      })
      // Don't flip to "connected" yet — wait for the actual connection
      // state event. If the restart succeeded, onconnectionstatechange
      // will fire with `connected` and reset the counter.
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      this.cfg.onLog?.('error', `ICE restart failed: ${msg}`)
      // If restart fails AND we haven't exhausted retries, schedule
      // another attempt; the next connectionstatechange may also re-trigger.
      if (this.iceRestartAttempts >= MAX_ICE_RESTARTS) {
        this.setConnectionState('lost')
      }
    } finally {
      this.iceRestartInFlight = false
    }
  }

  // Allow re-enabling media after construction (e.g., a viewer gets
  // promoted to cohost on a live stream and now needs to publish).
  enableMedia(audio: boolean, video: boolean) {
    this.cfg = { ...this.cfg, audio, video }
  }

  // Allow re-publishing (e.g., after promotion) — clears the
  // hasPublished guard so publish() actually runs.
  resetPublishedFlag() {
    this.hasPublished = false
  }

  // Tear down the existing RTCPeerConnection and rebuild it so a
  // freshly-promoted viewer can publish without two collisions:
  //
  //   1. Browser-side: the existing PC has accumulated recvonly
  //      transceivers + codec parameters from prior subscribeTo() calls.
  //      Calling addTransceiver(sendonly) + createOffer trips Chrome's
  //      session validator (ERROR_CONTENT, "Failed to set remote video
  //      description send parameters for m-section with mid='3'").
  //
  //   2. SFU-side: KonexZero (Cloudflare Calls underneath) binds our
  //      participant's DTLS fingerprint at the first /subscribe answer.
  //      A new client-side PC has a different fingerprint; the next
  //      /publish then 502s with "origin_bad_gateway" because the SFU
  //      can't reconcile the new SDP with the existing session.
  //
  // Both are fixed by /leave-ing on KZ first (which releases the SFU
  // session), re-/joining (which is idempotent — KZ upserts the
  // participant), and constructing a brand-new RTCPeerConnection with
  // the fresh ICE servers. After this returns, the caller should run
  // the recovery sweep so incoming peer streams get re-attached.
  async rejoinAsPublisher(): Promise<void> {
    // Close the local PC first so its DTLS heartbeats stop hitting KZ.
    if (this.pc) {
      try { this.pc.close() } catch { /* noop */ }
    }
    this.pc = null
    // Reset all peer-routing state — the new PC + new SFU session start
    // blank and we'll resubscribe through the normal path.
    this.subscribedPeerIds.clear()
    this.streamIdToPeerId.clear()
    this.remoteStreamsByPeer.forEach((s) => s.getTracks().forEach((t) => t.stop()))
    this.remoteStreamsByPeer.clear()
    this.published = {}
    this.hasPublished = false
    this.iceRestartAttempts = 0
    this.iceRestartInFlight = false
    if (this.disconnectedTimer != null) {
      window.clearTimeout(this.disconnectedTimer)
      this.disconnectedTimer = null
    }

    // Best-effort /leave to release the SFU-side session (DTLS binding,
    // existing transceivers). If KZ has already lost track of us,
    // ignore the failure and push on to /join — re-/join is idempotent.
    try {
      await this.api('POST', '/leave', undefined)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      this.cfg.onLog?.('info', `rejoinAsPublisher: /leave failed (continuing): ${msg}`)
    }

    // Re-/join with publisher_type=publisher. This is idempotent on KZ
    // — same participant_id just refreshes the ws_token and returns
    // fresh ICE servers (with rotated TURN credentials).
    const res = await this.api<JoinResponse>('POST', '/join', {
      display_name: this.cfg.displayName,
      room_type: this.cfg.roomType,
      participant_type: 'publisher'
    })
    this.cachedIceServers = res.data.ice_servers
    this.currentSessionId = res.data.session_id ?? null
    this.pc = this.buildPc(res.data.ice_servers)
  }

  // Mirror of rejoinAsPublisher() for the demotion path: a cohost is
  // either kicked from the stage or steps down voluntarily and becomes
  // a viewer again. Releases the SFU publish session, stops local
  // tracks (turns off the camera light), and reattaches as a
  // subscribe-only participant. Caller should run the recovery sweep
  // after this returns so existing publishers' streams come back.
  async rejoinAsViewer(): Promise<void> {
    if (this.pc) {
      try { this.pc.close() } catch { /* noop */ }
    }
    this.pc = null
    this.subscribedPeerIds.clear()
    this.remoteStreamsByPeer.forEach((s) => s.getTracks().forEach((t) => t.stop()))
    this.remoteStreamsByPeer.clear()
    // Stop local tracks so the camera light goes off and the mic stops
    // capturing. Without this the user remains "broadcasting" to
    // themselves even though their peer connection is gone.
    this.localStream?.getTracks().forEach((t) => t.stop())
    this.localStream = null
    this.published = {}
    this.hasPublished = false
    this.iceRestartAttempts = 0
    this.iceRestartInFlight = false
    if (this.disconnectedTimer != null) {
      window.clearTimeout(this.disconnectedTimer)
      this.disconnectedTimer = null
    }
    // Flip cfg so any later publish() / getUserMedia call no-ops.
    this.cfg = { ...this.cfg, audio: false, video: false }

    try {
      await this.api('POST', '/leave', undefined)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      this.cfg.onLog?.('info', `rejoinAsViewer: /leave failed (continuing): ${msg}`)
    }

    const res = await this.api<JoinResponse>('POST', '/join', {
      display_name: this.cfg.displayName,
      room_type: this.cfg.roomType,
      participant_type: 'viewer'
    })
    this.cachedIceServers = res.data.ice_servers
    this.currentSessionId = res.data.session_id ?? null
    this.pc = this.buildPc(res.data.ice_servers)
  }

  async getUserMedia(): Promise<MediaStream | null> {
    if (!this.cfg.audio && !this.cfg.video) return null

    // Race the getUserMedia promise against a hard timeout. Browsers
    // occasionally hang here — permission UI never resolves, hardware
    // enumeration stuck, NotReadableError swallowed by the runtime —
    // and without a timeout the host's "Go Live" tap just sits there
    // forever with no error to the user. 15s is generous; usual case
    // resolves in <1s.
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(
        () => reject(new Error(`getUserMedia timed out after ${GET_USER_MEDIA_TIMEOUT_MS}ms`)),
        GET_USER_MEDIA_TIMEOUT_MS
      )
    })
    let stream: MediaStream
    try {
      stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({ audio: this.cfg.audio, video: this.cfg.video }),
        timeoutPromise
      ])
    } finally {
      if (timeoutHandle) clearTimeout(timeoutHandle)
    }

    // Validate the returned stream: some browsers occasionally hand
    // back a stream with already-ended tracks (cam in use by another
    // app, transient hardware fail). Publishing such a stream looks
    // successful but no media flows — the host has to end+restart to
    // recover. Throw here so the caller's retry path triggers.
    const tracks = stream.getTracks()
    if (tracks.length === 0) {
      throw new Error('getUserMedia returned a stream with no tracks')
    }
    for (const t of tracks) {
      if (t.readyState !== 'live') {
        tracks.forEach((tr) => tr.stop())
        throw new Error(`getUserMedia track ${t.kind} not live (readyState=${t.readyState})`)
      }
    }

    this.localStream = stream
    return this.localStream
  }

  getLocalStream(): MediaStream | null {
    return this.localStream
  }

  setLocalTrackEnabled(kind: 'audio' | 'video', enabled: boolean): boolean {
    if (!this.localStream) return false
    let changed = false
    for (const t of this.localStream.getTracks()) {
      if (t.kind === kind && t.enabled !== enabled) { t.enabled = enabled; changed = true }
    }
    return changed
  }

  async publish(): Promise<void> {
    // The SDP exchange holds the negotiation lock — the PC sits in
    // have-local-offer until the answer applies, so a concurrent
    // subscribe negotiation would glare. The bytesSent wait + confirm
    // run outside the lock: they do no SDP work and would otherwise
    // block subscribes for up to 3s.
    const confirmNames = await this.withNegotiationLock(() => this.publishExchange())
    if (confirmNames && confirmNames.length > 0) {
      await this.confirmPublished(confirmNames)
    }
  }

  // The /publish SDP exchange. Returns the published track names (for
  // /publish/confirm) or null when there was nothing to publish.
  private async publishExchange(): Promise<string[] | null> {
    if (!this.pc) throw new Error('not joined')
    if (this.hasPublished) return null
    if (!this.localStream) return null // nothing to publish (e.g. viewer)

    const inputs: { track_name: string; kind: 'audio' | 'video'; mid: string }[] = []

    if (this.cfg.audio) {
      const t = this.localStream.getAudioTracks()[0]
      if (t) {
        const trans = this.pc.addTransceiver(t, { direction: 'sendonly' })
        this.published['mic'] = trans
        inputs.push({ track_name: 'mic', kind: 'audio', mid: '' })
      }
    }
    if (this.cfg.video) {
      const t = this.localStream.getVideoTracks()[0]
      if (t) {
        const trans = this.pc.addTransceiver(t, { direction: 'sendonly' })
        this.published['cam'] = trans
        inputs.push({ track_name: 'cam', kind: 'video', mid: '' })
      }
    }
    if (inputs.length === 0) return null

    const offer = await this.pc.createOffer()
    await this.pc.setLocalDescription(offer)
    for (const i of inputs) {
      const trans = this.published[i.track_name]
      if (trans?.mid) i.mid = trans.mid
    }

    // Retry the /publish round-trip on transient SFU errors (502, 503).
    // The api() layer already retries 502 once with 250ms; this outer
    // loop adds 3 more attempts with longer waits, covering the case
    // where Cloudflare Calls itself is briefly unavailable.
    // setLocalDescription is idempotent on the same offer; we don't
    // recreate it on retry.
    const publishBackoff = [ 750, 2000, 5000 ]
    let lastErr: Error | undefined
    for (let attempt = 0; attempt <= publishBackoff.length; attempt++) {
      try {
        const res = await this.api<PublishResponse>('POST', '/publish', {
          tracks: inputs,
          session_description: { type: offer.type, sdp: offer.sdp }
        })
        await this.pc.setRemoteDescription(res.data.session_description)
        this.hasPublished = true
        // Fast Connect (KZ Phase 10): tracks are announced to the room at
        // /publish/confirm, NOT at /publish — publish() runs that next.
        return inputs.map((i) => i.track_name)
      } catch (e) {
        lastErr = e instanceof Error ? e : new Error(String(e))
        const transient = lastErr instanceof ApiError
          ? lastErr.status === 502 || lastErr.status === 503 || lastErr.status === 504
          : /→ 50[234]/.test(lastErr.message) || /NetworkError|Failed to fetch/i.test(lastErr.message)
        if (!transient || attempt === publishBackoff.length) {
          this.cfg.onLog?.('error', `publish failed permanently: ${lastErr.message}`)
          throw lastErr
        }
        const wait = publishBackoff[attempt]
        this.cfg.onLog?.('info', `publish retry ${attempt + 1}/${publishBackoff.length} (waiting ${wait}ms) after ${lastErr.message.split(': ')[0]}`)
        await new Promise((r) => setTimeout(r, wait))
      }
    }
    if (lastErr) throw lastErr
    return null
  }

  // Resolves true once every named published track reports outbound-rtp
  // bytesSent > 0 (RTP actually flowing), false on timeout. Mirrors the
  // upstream reference SDK / Cloudflare's partytracks client, which gate
  // track announcement on bytesSent. Typical resolve time is 100–300ms
  // after the publish answer is applied.
  private async waitForTracksLive(trackNames: string[], timeoutMs = 3_000): Promise<boolean> {
    const senders = trackNames
      .map((n) => this.published[n]?.sender)
      .filter((s): s is RTCRtpSender => !!s)
    if (senders.length === 0) return true

    const deadline = performance.now() + timeoutMs
    let delay = 10
    for (;;) {
      const liveChecks = await Promise.all(
        senders.map(async (sender) => {
          const stats = await sender.getStats()
          let live = false
          stats.forEach((s) => {
            const r = s as { type?: string; bytesSent?: number }
            if (r.type === 'outbound-rtp' && (r.bytesSent ?? 0) > 0) live = true
          })
          return live
        })
      )
      if (liveChecks.every(Boolean)) return true
      if (performance.now() >= deadline) return false
      await new Promise((r) => setTimeout(r, delay))
      delay = Math.min(delay * 2, 100)
    }
  }

  // Announce published tracks to the room via POST /publish/confirm once
  // media is flowing (KZ Phase 10 "Fast Connect" contract). KZ fires the
  // track_published room event here — a publisher that never confirms is
  // invisible to subscribers gating on that event, and pulling its tracks
  // pre-confirm risks the SFU's empty-track error.
  //
  // Best-effort by design: this runs inside publish()'s retry loop, and a
  // confirm failure must NOT re-trigger /publish (KZ /publish is not
  // idempotent — see KONEXZERO_GAPS.md N8). On persistent failure we log
  // and move on; subscribers fall back to their tracks_not_ready retries.
  private async confirmPublished(trackNames: string[]): Promise<void> {
    if (trackNames.length === 0) return
    const rtpFlowing = await this.waitForTracksLive(trackNames)
    if (!rtpFlowing) {
      this.cfg.onLog?.('info', 'confirm: outbound RTP not detected within 3s — confirming anyway (subscriber retries are the fallback)')
    }
    const confirmBackoff = [ 0, 500, 1500 ]
    for (let attempt = 0; attempt < confirmBackoff.length; attempt++) {
      if (confirmBackoff[attempt] > 0) await new Promise((r) => setTimeout(r, confirmBackoff[attempt]))
      try {
        await this.api('POST', '/publish/confirm', { track_names: trackNames })
        this.cfg.onLog?.('info', `confirmed tracks live: [${trackNames.join(', ')}]`)
        return
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        if (attempt === confirmBackoff.length - 1) {
          this.cfg.onLog?.('error', `publish/confirm failed after ${confirmBackoff.length} attempts: ${msg} — peers must rely on tracks_not_ready retries`)
        } else {
          this.cfg.onLog?.('info', `publish/confirm retry ${attempt + 1}/${confirmBackoff.length - 1}: ${msg.split(': ')[0]}`)
        }
      }
    }
  }

  async subscribeTo(subscriptions: { participant_id: string; track_names: string[]; track_kinds?: ('audio' | 'video')[] }[]): Promise<void> {
    if (!this.pc) throw new Error('not joined')
    if (subscriptions.length === 0) return

    // Filter out peers we've already subscribed to so cable + sweep
    // sources can fire without doubling up the SDP exchange.
    const fresh = subscriptions.filter((s) => !this.subscribedPeerIds.has(s.participant_id))
    if (fresh.length === 0) {
      this.cfg.onLog?.('info', `subscribeTo: all ${subscriptions.length} peers already subscribed, skipping`)
      return
    }

    // PER-PEER SDP exchange. Each /subscribe call carries one peer so
    // the response SDP's `a=msid` lines map deterministically to that
    // peer (otherwise we'd have to guess which m-section belongs to
    // which subscribed participant, with no reliable signal because
    // KZ's stream IDs are opaque to us). The latency cost is N HTTP
    // round trips instead of 1, but each is ~100-300ms and we recover
    // from per-peer failures cleanly. Critically, this is the only way
    // to make ontrack routing reliable when there are 2+ subscribed
    // peers — the previous batched approach silently dropped tracks
    // whose stream IDs didn't match the fuzzy heuristic.
    let firstErr: Error | undefined
    for (const sub of fresh) {
      try {
        await this._subscribeOne(sub)
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e))
        if (!firstErr) firstErr = err
        this.cfg.onLog?.('error', `subscribe to ${sub.participant_id} failed: ${err.message}`)
        // Continue with remaining peers — one failure shouldn't strand
        // the others. Caller (sweep / cable handler) retries failed peers.
      }
    }
    if (firstErr) throw firstErr
  }

  // Subscribe to a single peer with retry + SDP-msid registration.
  // Retries with progressive backoff on the race conditions Cloudflare
  // Calls exhibits: 503 tracks_not_ready (typed), 404/422 (legacy),
  // 502 origin_bad_gateway (transient CF Calls hiccup).
  //
  // Two flows (KZ Phase 10 "Fast Connect"):
  //   - Fast (single round trip): when the caller knows each track's
  //     kind, we pre-add recvonly transceivers, send our own offer with
  //     the /subscribe request, and the response is an SDP answer —
  //     /subscribe/answer disappears.
  //   - Legacy (two round trips): kinds unknown → /subscribe returns the
  //     SFU's offer; we answer via /subscribe/answer.
  private async _subscribeOne(sub: { participant_id: string; track_names: string[]; track_kinds?: ('audio' | 'video')[] }): Promise<void> {
    if (!this.pc) throw new Error('not joined')

    // Mark as subscribed BEFORE the SDP exchange so concurrent calls
    // (e.g., cable handler + sweep firing at the same time) dedup
    // against the in-flight subscribe.
    this.subscribedPeerIds.add(sub.participant_id)

    let useFastPath = !!sub.track_kinds && sub.track_kinds.length === sub.track_names.length

    const backoff = [ 500, 1500, 3000, 6000, 12_000 ]
    let lastErr: Error | undefined
    for (let attempt = 0; attempt < backoff.length; attempt++) {
      try {
        if (useFastPath) {
          await this.subscribeOneFast(sub.participant_id, sub.track_names, sub.track_kinds!)
        } else {
          await this.subscribeOneLegacy(sub.participant_id, sub.track_names)
        }
        return
      } catch (e) {
        lastErr = e instanceof Error ? e : new Error(String(e))
        if (useFastPath && lastErr.message.startsWith('fast-subscribe-unsupported')) {
          // Upstream ignored the inline offer (pre-Phase-10 deploy).
          // Fall back to the two-step flow for the remaining attempts.
          useFastPath = false
          this.cfg.onLog?.('info', `subscribe ${sub.participant_id}: upstream did not answer the inline offer — falling back to two-step flow`)
          continue
        }
        const isRace = lastErr instanceof ApiError
          ? (lastErr.status === 503 && lastErr.code === 'tracks_not_ready') ||
            lastErr.status === 404 || lastErr.status === 422 || lastErr.status === 502
          : lastErr.message.includes('→ 404') || lastErr.message.includes('→ 422') ||
            lastErr.message.includes('→ 502') || lastErr.message.includes('→ 503')
        if (!isRace || attempt === backoff.length - 1) {
          // Evict so the next sweep / cable event can re-attempt cleanly.
          this.subscribedPeerIds.delete(sub.participant_id)
          for (const [sid, pid] of this.streamIdToPeerId.entries()) {
            if (pid === sub.participant_id) this.streamIdToPeerId.delete(sid)
          }
          throw lastErr
        }
        // Prefer the server's typed retry hint (Retry-After-Ms on 503
        // tracks_not_ready) over the fixed schedule. Clamped: KZ's hint
        // can be optimistic about cross-region SFU edge propagation
        // (see KONEXZERO_GAPS.md N7), and the outer sweep/watchdog
        // recovery covers us if the attempts exhaust early.
        const hint = lastErr instanceof ApiError && lastErr.retryAfterMs != null
          ? Math.min(Math.max(lastErr.retryAfterMs, 250), 15_000)
          : undefined
        const wait = hint ?? backoff[attempt]
        this.cfg.onLog?.('info', `subscribe ${sub.participant_id} retry ${attempt + 1}/${backoff.length} (backoff ${wait}ms${hint != null ? ', server hint' : ''}) after ${lastErr.message.split(': ')[0]}`)
        await new Promise((r) => setTimeout(r, wait))
      }
    }
    if (lastErr) {
      this.subscribedPeerIds.delete(sub.participant_id)
      for (const [sid, pid] of this.streamIdToPeerId.entries()) {
        if (pid === sub.participant_id) this.streamIdToPeerId.delete(sid)
      }
      throw lastErr
    }
  }

  // Single-round-trip subscribe (KZ Phase 10): pre-add one recvonly
  // transceiver per track, send our own offer with /subscribe, and apply
  // the SDP answer the response carries. The whole attempt runs inside
  // the negotiation lock and rolls the PC back to a stable state on any
  // failure, so retry waits never strand the PC in have-local-offer
  // (which would block every other negotiation).
  private async subscribeOneFast(
    participantId: string,
    trackNames: string[],
    trackKinds: ('audio' | 'video')[]
  ): Promise<void> {
    await this.withNegotiationLock(async () => {
      if (!this.pc) throw new Error('not joined')
      const pc = this.pc
      const added: RTCRtpTransceiver[] = []
      for (const kind of trackKinds) {
        const trans = pc.addTransceiver(kind, { direction: 'recvonly' })
        // Cloudflare's SFU mishandles dual-profile H265 offers: its answer
        // drops one H265 payload but keeps that payload's RTX entry, whose
        // dangling apt= reference makes Chrome reject setRemoteDescription.
        // Excluding H265 from the recvonly offer avoids the broken answer.
        if (kind === 'video' && typeof trans.setCodecPreferences === 'function') {
          const caps = RTCRtpReceiver.getCapabilities('video')
          const codecs = caps?.codecs.filter((c) => !/h265|hevc/i.test(c.mimeType)) ?? []
          if (codecs.length > 0) trans.setCodecPreferences(codecs)
        }
        added.push(trans)
      }
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      try {
        const res = await this.api<SubscribeResponse>('POST', '/subscribe', {
          subscriptions: [{ participant_id: participantId, track_names: trackNames }],
          session_description: { type: offer.type, sdp: offer.sdp }
        })
        const sd = res.data.session_description
        if (sd?.type !== 'answer') {
          throw new Error('fast-subscribe-unsupported: /subscribe response carried no SDP answer')
        }
        // Register stream IDs in the routing ledger BEFORE applying the
        // answer — ontrack can fire as soon as it is applied. In the fast
        // path the SFU is the sender, so its answer carries the a=msid
        // lines for the streams it will push to us.
        const streamIds = extractStreamIdsFromSdp(sd.sdp ?? null)
        for (const sid of streamIds) {
          this.streamIdToPeerId.set(sid, participantId)
        }
        if (streamIds.length === 0) {
          this.cfg.onLog?.('info', `subscribe ${participantId}: no msid lines in answer SDP — routing will fall through to single-peer / fuzzy`)
        } else {
          this.cfg.onLog?.('info', `subscribe ${participantId}: pre-registered stream ids [${streamIds.join(', ')}] (1-RT)`)
        }
        await pc.setRemoteDescription(sd)
      } catch (e) {
        // Restore a stable signaling state before releasing the lock.
        // Rollback drops the pending local offer; the transceivers we
        // added survive rollback (per spec) so stop them explicitly —
        // the next attempt adds fresh ones.
        try { await pc.setLocalDescription({ type: 'rollback' }) } catch { /* already stable */ }
        for (const t of added) {
          try { t.stop() } catch { /* noop */ }
        }
        throw e
      }
    })
  }

  // Legacy two-round-trip subscribe: /subscribe returns the SFU's offer,
  // we answer locally and complete via /subscribe/answer. Used when the
  // caller doesn't know the track kinds (so we can't pre-build recvonly
  // transceivers) or when the upstream ignores inline offers.
  private async subscribeOneLegacy(participantId: string, trackNames: string[]): Promise<void> {
    const res = await this.api<SubscribeResponse>('POST', '/subscribe', {
      subscriptions: [{ participant_id: participantId, track_names: trackNames }]
    })

    // Extract stream IDs from the offer SDP and register them in the
    // routing ledger BEFORE setRemoteDescription. ontrack can fire as
    // soon as the remote description is applied, so the mapping must be
    // in place by then.
    const streamIds = extractStreamIdsFromSdp(res.data.session_description.sdp ?? null)
    for (const sid of streamIds) {
      this.streamIdToPeerId.set(sid, participantId)
    }
    if (streamIds.length === 0) {
      this.cfg.onLog?.('info', `subscribe ${participantId}: no msid lines in offer SDP — routing will fall through to single-peer / fuzzy`)
    } else {
      this.cfg.onLog?.('info', `subscribe ${participantId}: pre-registered stream ids [${streamIds.join(', ')}]`)
    }

    const answer = await this.withNegotiationLock(async () => {
      if (!this.pc) throw new Error('not joined')
      await this.pc.setRemoteDescription(res.data.session_description)
      const a = await this.pc.createAnswer()
      await this.pc.setLocalDescription(a)
      return a
    })
    await this.api('POST', '/subscribe/answer', {
      session_description: { type: answer.type, sdp: answer.sdp }
    })
  }

  // Forget a peer who has left so a future rejoin re-subscribes cleanly.
  // Without this, subscribedPeerIds keeps the stale id and subscribeTo()'s
  // "already subscribed" guard short-circuits on rejoin → no media flows.
  // Also drops the cached MediaStream so ontrack creates a fresh one
  // (otherwise new tracks get appended to a stream the UI has already
  // detached and the tile stays blank) AND drops any stream-id → peer
  // mappings for this participant from the routing ledger so the next
  // subscribe gets a clean slate.
  forgetPeer(participantId: string): void {
    this.subscribedPeerIds.delete(participantId)
    for (const [sid, pid] of this.streamIdToPeerId.entries()) {
      if (pid === participantId) this.streamIdToPeerId.delete(sid)
    }
    const stream = this.remoteStreamsByPeer.get(participantId)
    if (stream) {
      stream.getTracks().forEach((t) => t.stop())
      this.remoteStreamsByPeer.delete(participantId)
    }
  }

  async leave(): Promise<void> {
    if (this.hasLeft) return
    this.hasLeft = true
    if (this.disconnectedTimer != null) {
      window.clearTimeout(this.disconnectedTimer)
      this.disconnectedTimer = null
    }
    try {
      await this.api('POST', '/leave', undefined)
    } catch {
      /* best effort */
    }
    this.pc?.close()
    this.localStream?.getTracks().forEach((t) => t.stop())
    this.remoteStreamsByPeer.forEach((s) => s.getTracks().forEach((t) => t.stop()))
    this.pc = null
    this.localStream = null
    this.remoteStreamsByPeer.clear()
    this.subscribedPeerIds.clear()
    this.streamIdToPeerId.clear()
    this.setConnectionState('closed')
  }

  async getRtt(): Promise<number | null> {
    if (!this.pc) return null
    const stats = await this.pc.getStats()
    let selected: { currentRoundTripTime?: number; selected?: boolean; nominated?: boolean } | undefined
    stats.forEach((raw) => {
      const s = raw as { type: string; selected?: boolean; nominated?: boolean; currentRoundTripTime?: number }
      if (s.type === 'candidate-pair' && (s.selected || s.nominated)) {
        if (!selected || s.selected) selected = s
      }
    })
    if (!selected || typeof selected.currentRoundTripTime !== 'number') return null
    return Math.round(selected.currentRoundTripTime * 1000)
  }

  private async api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.cfg.basePath}/rooms/${encodeURIComponent(this.cfg.roomKey)}${path}`
    const csrf = (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement | null)?.content ?? ''
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Participant-Id': this.cfg.participantId,
      'X-CSRF-Token': csrf,
      'X-Requested-With': 'XMLHttpRequest'
    }
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    let lastErr: Error | undefined
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt > 0) {
        this.cfg.onLog?.('info', `retry ${method} ${path} after 502`)
        await new Promise((r) => setTimeout(r, 250))
      } else {
        this.cfg.onLog?.('request', `${method} ${path}`)
      }
      const response = await fetch(url, {
        method,
        headers,
        credentials: 'same-origin',
        body: body === undefined ? undefined : JSON.stringify(body)
      })
      const text = await response.text()
      let parsed: unknown
      try { parsed = text.length > 0 ? JSON.parse(text) : null } catch { parsed = text }
      this.cfg.onLog?.(response.ok ? 'response' : 'error', `${response.status} ${method} ${path}`)
      if (response.ok) return parsed as T
      const detail = (typeof parsed === 'object' && parsed !== null && ('detail' in parsed || 'error' in parsed))
        ? String((parsed as { detail?: unknown; error?: unknown }).detail ?? (parsed as { error?: unknown }).error)
        : text || response.statusText
      const code = (typeof parsed === 'object' && parsed !== null && 'code' in parsed)
        ? String((parsed as { code?: unknown }).code ?? '') || undefined
        : undefined
      const retryAfterRaw = Number(response.headers.get('Retry-After-Ms') ?? '')
      const retryAfterMs = Number.isFinite(retryAfterRaw) && retryAfterRaw > 0 ? retryAfterRaw : undefined
      lastErr = new ApiError(`${method} ${path} → ${response.status}: ${detail}`, response.status, code, retryAfterMs)
      if (response.status !== 502) throw lastErr
    }
    throw lastErr ?? new Error(`${method} ${path} failed after retry`)
  }
}

